/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import get from 'lodash/get';
import { castToMoment } from 'helpers/index';
import { apiGetDocumentById } from 'api'
import { useList } from 'context/ResourceProvider/hooks/useList';
import { ComplianceMappings } from 'mappers/ComplianceDataMapper';
import { INITIAL_VALUES } from '../index';

export default (Component) => {
  return React.forwardRef((props, ref) => {
    const { metadata, documents, featureFlags, ...restProps } = props;
    const [documentDetails, setDocumentDetails] = useState(null);
    const [fetching, setFetching] = useState(false);
    const thisDocument = documentDetails || documents?.[0];

    const firstDocumentId = React.useMemo(() => {
      if (documents.length > 1) {
        return null;
      }
      const { documentListId, documentList } = documents[0];
      if (documentListId) return documentListId;
      if (documentList) return documentList.id;

      return documents[0].id;
    }, [documents]);

    const { data: testData, loading: testDataLoading } = useList({
      resource: 'document-list-compliance-test-data',
      meta: {
        params: {
          id: firstDocumentId,
        },
      },
      skip: !firstDocumentId,
    });

    const { data: certificateData, loading: certificateDataLoading } = useList({
      resource: 'document-list-compliance-certificate-data',
      meta: {
        params: {
          id: firstDocumentId,
        },
      },
      skip: !firstDocumentId,
    });

    const { data: otherData, loading: otherDataLoading } = useList({
      resource: 'document-list-compliance-other-data',
      meta: {
        params: {
          id: firstDocumentId,
        },
      },
      skip: !firstDocumentId,
    });

    const complianceLoading = React.useMemo(() => {
      return testDataLoading || certificateDataLoading || otherDataLoading;
    }, [testDataLoading, certificateDataLoading, otherDataLoading]);

    const complianceData = React.useMemo(() => {
      if (featureFlags && featureFlags.includes('compliance-data')) {
        const _testData = get(testData, 'data.data', []);
        const _certificateData = get(certificateData, 'data.data', []);
        const _otherData = get(otherData, 'data.data', []);
        return {
          testData: (_testData || []).map((item) => ComplianceMappings.mapTestDataToView(item)),
          certificateData: (_certificateData || []).map((item) =>
            ComplianceMappings.mapCertificateDataToView(item)
          ),
          otherData: (_otherData || []).map((item) => ComplianceMappings.mapOtherDataToView(item)),
        };
      }
      return null;
    }, [testData, certificateData, otherData]);

    const previousFactories = React.useMemo(() => {
      if (documents.length > 1) {
        return [];
      }
      const factories = get(thisDocument, 'factories', []);
      return factories;
    }, [documents, fetching]);

    const previousSuppliers = React.useMemo(() => {
      if (documents.length > 1) {
        return [];
      }
      const suppliers = get(thisDocument, 'suppliers', []);
      return suppliers;
    }, [documents, fetching]);

    const previousProducts = React.useMemo(() => {
      if (documents.length > 1) {
        return [];
      }
      const products = get(thisDocument, 'products', []);
      return products;
    }, [documents, fetching]);

    const initialValues = React.useMemo(() => {
      if (documents.length > 1) {
        return INITIAL_VALUES;
      }
      const firstDocument = documents[0];

      const convertTypeToOption = (type) => {
        if (!type) return [];
        return [
          {
            label: type.name,
            value: type.id,
          },
        ];
      };

      const initial = {
        type: convertTypeToOption(firstDocument.type),
        saveTo: {
          products: (previousProducts || []).map(({ ...rest }) => ({ ...rest, deletable: false })),
          suppliers: (previousSuppliers || []).map(({ ...rest }) => ({
            ...rest,
            deletable: false,
          })),
          factories: (previousFactories || []).map(({ ...rest }) => ({
            ...rest,
            deletable: false,
          })),
        },
        reminder: {
          issueDate: castToMoment(firstDocument.issueDate),
          expiryDate: castToMoment(firstDocument.expiryDate),
          reviewDate: castToMoment(firstDocument.reviewDate),
          reminderDate: castToMoment(firstDocument.reminderDate),
          reminderType: firstDocument.reminderType,
          note: firstDocument.note,
        },
      };
      if (featureFlags && featureFlags.includes('compliance-data')) {
        return {
          ...initial,
          complianceData: {
            ...complianceData,
          },
        };
      }
      return initial;
    }, [documents, complianceData, fetching]);
    const fetchDocumentDetails = async (tId) => {
      setFetching(true);
      try {
        const res = await apiGetDocumentById(tId);
        setDocumentDetails(res?.data?.data || null);
        setFetching(false);
      } catch (error) {
        setDocumentDetails(null);
        setFetching(false);
      }
    }

    useEffect(() => {
      if (firstDocumentId) {
        fetchDocumentDetails(firstDocumentId);
      }
    }, [firstDocumentId])

    return (
      <React.Suspense fallback={<Spin />}>
        <Component
          {...restProps}
          initialValues={{
            ...initialValues,
            files: documents,
          }}
          featureFlags={featureFlags || ['saveTo', 'reminder']}
          loading={complianceLoading}
          ref={ref}
        />
      </React.Suspense>
    );
  });
};
